import React, { useState, useRef, useEffect } from 'react';

const WHATSAPP_NUMBER = '917416067676';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}`;
const WHATSAPP_QR = '/whatsapp-qr-code.png';

const WhatsAppIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="26" height="26" fill="#fff" aria-hidden="true">
    <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.469 2.027 7.773L0 32l8.489-2.001A15.93 15.93 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.771-1.853l-.485-.288-5.037 1.188 1.224-4.905-.317-.503A13.24 13.24 0 0 1 2.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.27-9.878c-.398-.199-2.355-1.162-2.72-1.295-.365-.133-.63-.199-.895.199-.266.398-1.029 1.295-1.261 1.561-.232.266-.464.299-.862.1-.398-.199-1.681-.619-3.203-1.977-1.184-1.057-1.983-2.362-2.215-2.76-.232-.398-.025-.613.174-.811.179-.178.398-.464.597-.696.199-.232.266-.398.398-.664.133-.266.066-.498-.033-.697-.1-.199-.895-2.158-1.228-2.955-.323-.776-.651-.671-.895-.683l-.763-.013c-.266 0-.697.1-1.062.498-.365.398-1.394 1.362-1.394 3.32 0 1.958 1.427 3.85 1.627 4.116.199.266 2.808 4.288 6.805 5.854.951.366 1.693.584 2.271.748.954.271 1.823.233 2.51.141.766-.101 2.355-.963 2.688-1.893.333-.93.333-1.727.233-1.893-.099-.166-.365-.266-.763-.465z" />
  </svg>
);

const FloatingWhatsAppWidget = () => {
  const [qrExpanded, setQrExpanded] = useState(false);
  const [qrHovered, setQrHovered] = useState(false);
  const buttonRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(72);

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth);
    }
  }, []);

  return (
    <>
      {/* Expanded QR overlay — click outside to close */}
      {qrExpanded && (
        <div
          onClick={() => setQrExpanded(false)}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9997,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: '16px',
              padding: '20px',
              boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <img
              src={WHATSAPP_QR}
              alt="WhatsApp QR Code"
              style={{ width: '220px', height: '220px', borderRadius: '8px' }}
            />
            <p style={{ margin: 0, fontWeight: 600, fontSize: '14px', color: '#333' }}>
              Scan to chat on WhatsApp
            </p>
            <button
              onClick={() => setQrExpanded(false)}
              style={{
                background: 'none',
                border: '1px solid #ccc',
                borderRadius: '8px',
                padding: '6px 16px',
                cursor: 'pointer',
                fontSize: '13px',
                color: '#555',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Fixed bottom-right stack */}
      <div
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          zIndex: 9998,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: '8px',
        }}
      >
        {/* Label — only visible on QR hover */}
        <span style={{
          color: '#000',
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          transition: 'opacity 0.2s ease',
          pointerEvents: 'none',
        }}>
          {qrHovered ? 'Send WhatsApp' : 'WhatsApp'}
        </span>

        {/* Small QR thumbnail — click to expand, hover scales to 2x */}
        <div
          onClick={() => setQrExpanded(true)}
          title="Scan to chat on WhatsApp"
          style={{
            width: qrHovered ? `${buttonWidth}px` : '72px',
            height: qrHovered ? `${buttonWidth}px` : '72px',
            borderRadius: '12px',
            overflow: 'hidden',
            cursor: 'pointer',
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
            border: '3px solid #25D366',
            transition: 'width 0.25s ease, height 0.25s ease, transform 0.2s',
            background: '#fff',
          }}
          onMouseEnter={() => setQrHovered(true)}
          onMouseLeave={() => setQrHovered(false)}
        >
          <img
            src={WHATSAPP_QR}
            alt="WhatsApp QR"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            loading="lazy"
            decoding="async"
          />
        </div>

        {/* Chat with us button */}
        <a
          ref={buttonRef}
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Chat on WhatsApp"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            backgroundColor: '#25D366',
            color: '#fff',
            borderRadius: '50px',
            padding: '12px 20px',
            boxShadow: '0 4px 16px rgba(37,211,102,0.45)',
            textDecoration: 'none',
            fontFamily: 'sans-serif',
            fontWeight: 600,
            fontSize: '15px',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 6px 24px rgba(37,211,102,0.6)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 4px 16px rgba(37,211,102,0.45)';
          }}
        >
          <WhatsAppIcon />
          Chat
        </a>
      </div>
    </>
  );
};

export default FloatingWhatsAppWidget;
